import { SubmitHelpers } from '@area2k/use-form';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';

import PoolWorkerAutocompleteField from './PoolWorkerAutocompleteField';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import Form from '@/form';
import { WorkerItem } from '@/routes/Agency/Job/WorkerAutocompleteField';
import {
  PreferredPoolsAPI,
  extractPoolsError,
} from '@/services/preferredPoolsAPI';

// "Add worker to pool" (plan F7, prototype .cd-add-worker-btn). Reason is
// OPTIONAL (OQ-2). The typeahead hides BLOCKED workers (P14 UX); the backend
// re-enforces the block with a 422 either way (edge #11), which surfaces in
// the error alert. A pending nominee added here is superseded server-side
// (P12).

type Props = {
  poolId: number;
  customerId: string;
  skillId?: number;
  onClose: () => void;
  onSuccess: () => void;
};

type FormValues = {
  worker: WorkerItem | null;
};

const AddWorkerModal = ({
  poolId,
  customerId,
  skillId,
  onClose,
  onSuccess,
}: Props) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (
    values: FormValues,
    _helpers: SubmitHelpers,
  ): Promise<void> => {
    if (!values.worker) return;
    setLoading(true);
    setError(null);
    try {
      await PreferredPoolsAPI.addMember(poolId, {
        worker_id: Number(values.worker.id),
        reason: reason.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(extractPoolsError(err, 'Failed to add worker to pool'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Add worker to pool"
      onRequestClose={onClose}
    >
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon={faExclamationTriangle}
            status="danger"
            title="Error"
          />
        )}
        <Form initialValues={{ worker: null }} onSubmit={handleSubmit}>
          <PoolWorkerAutocompleteField
            customerId={customerId}
            fieldId="worker"
            label="Select GravyWorker"
            skillId={skillId ? String(skillId) : undefined}
          />
          <FormElement label="Reason (optional)">
            <TextArea
              placeholder="Add a reason for the audit trail."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </FormElement>
          <Stack justify="end">
            <Button
              a11yLabel="Cancel"
              appearance="outline"
              label="Cancel"
              type="button"
              onClick={onClose}
            />
            {/* a11yLabel matches the visible label (WCAG 2.5.3 Label in
                Name). It previously read "Add worker to pool" while the
                button said "Add to pool", which also collided with the card's
                own "Add worker to pool" trigger — two different controls with
                one accessible name, so voice control and name-based
                automation could not tell them apart. */}
            <Button
              a11yLabel="Add to pool"
              isLoading={loading}
              label="Add to pool"
              type="submit"
            />
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default AddWorkerModal;
